import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import adminService from '../../services/adminService';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

export default function AdminProgramsPage() {
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setLoading(true);
        const stats = await adminService.getProgramStats();
        setPrograms(stats.mostPopular);
      } catch (err) {
        console.error('Error loading programs:', err);
        setError('Failed to load loyalty programs');
      } finally {
        setLoading(false);
      }
    };

    loadPrograms();
  }, []);

  const filteredPrograms = programs.filter(program =>
    program.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPrograms = programs.length;
  const avgUsage = programs.length > 0 
    ? Math.round(programs.reduce((sum, p) => sum + p.usageCount, 0) / programs.length)
    : 0;

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Loyalty Programs
      </Typography>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard>
            <Typography color="textSecondary" gutterBottom>
              Total Programs
            </Typography>
            <Typography variant="h4" sx={{ color: '#4FD1C7', fontWeight: 700 }}>
              {totalPrograms}
            </Typography>
          </StatCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard>
            <Typography color="textSecondary" gutterBottom>
              Avg Users per Program
            </Typography>
            <Typography variant="h4" sx={{ color: '#4FD1C7', fontWeight: 700 }}>
              {avgUsage}
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Programs Table */}
      <StyledCard>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search programs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(79, 209, 199, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Program Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Users Tracking</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Popularity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrograms.length > 0 ? (
                    filteredPrograms.map((program) => (
                      <TableRow key={program.name} hover>
                        <TableCell>{program.name}</TableCell>
                        <TableCell align="right">{program.usageCount}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${Math.round((program.usageCount / Math.max(...programs.map(p => p.usageCount), 1)) * 100)}%`}
                            size="small"
                            color={program.usageCount > avgUsage ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        No programs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
}
